import { API_URL } from "env";

import { mixLogOut } from '../services/mixin';
import { createRequestCache } from '../utils/requestCache';
import type { AdInfo, MessageCreator } from './ChannelMessage';
import type { AppointmentNextTag } from './Appointment';
export interface ChannelCustomerItem {
    /** API Telesales: mảng và phiếu nhu cầu (kênh khách muốn nằm trong needs.kenh) */
    mang?: string | null;
    needs?: Record<string, any> | null;
    /** API Telesales: khách có khoản cọc đã nhận */
    has_received_deposit?: boolean;
    /** API Telesales: tóm tắt lịch sử chạm trên server, để máy khác tính đúng "Việc tiếp theo" */
    last_touch?: { at: string; kind: string; code?: string | null } | null;
    last_call?: { at: string; code?: string | null; direction?: "in" | "out" } | null;
    last_msg_at?: string | null;
    out_call_count?: number | null;
    missed_since_connected?: number | null;
    id: number;
    display_name: string;
    external_user_id: string;
    avatar?: string;
    avatar_url?: string;
    unread_count: number;
    last_message_text: string;
    /** Tin nhắn cuối, kèm nhân viên đã gửi (creator/staff_name null nếu khách gửi hoặc gửi ngoài CRM) */
    last_message?: {
        id: number;
        customer_id: number;
        created_by_id?: number | null;
        direction?: "inbound" | "outbound" | string | null;
        sent_at?: string | null;
        creator?: MessageCreator | null;
        staff_name?: string | null;
    } | null;
    /** Tên nhân viên đã gửi tin nhắn cuối (giống last_message.staff_name) */
    last_message_staff_name?: string | null;
    // Hướng của tin nhắn cuối: "outbound" = nhân viên gửi, "inbound" = khách gửi (chưa trả lời)
    direction?: "inbound" | "outbound" | string | null;
    last_seen_at: string;
    province_code?: string;
    ward_code?: string;
    connection?: { display_name: string };
    province?: { code: string; name: string; full_name: string } | null;
    ward?: { code: string; name: string; full_name: string } | null;
    // Nhân viên trực page (in_charge_staff) và telesale (contact_staff).
    // staff/telesale là alias tương thích ngược, API trả cả hai cặp.
    staff?: { id: number; name: string } | null;
    telesale?: { id: number; name: string } | null;
    in_charge_staff?: { id: number; name: string } | null;
    contact_staff?: { id: number; name: string } | null;
    customer_tag?: { id: number; name: string; color?: string | null } | null;
    /** Thẻ được phép chuyển sang, backend tính theo ma trận (phòng ban + screen_context = 4 "Khách hàng"). */
    dynamic_next_tags?: AppointmentNextTag[] | null;
    customer_care_status?: { id: number; name: string; code: string; color: string } | null;
    customer_care_status_id?: number | null;
    // Quan hệ nhiều-nhiều; customer_care_status_id chỉ là trạng thái chính
    care_statuses?: CustomerCareStatus[];
    is_priority_contact?: boolean;
    customer?: { id: number; [key: string]: any } | null;
    profile_link?: string | null; // Link trang cá nhân của khách trên kênh (FB/Instagram...)
    is_spam?: number;
    is_ping?: number;
    type?: string; // 'chat' | 'comment'
    spam_logs?: CustomerSpam[];
    // Các trường của API danh sách khách hàng (GET /customers)
    customer_code?: string | null;
    fullname?: string | null;
    phone?: string | null;
    phones?: { id: number; phone: string; is_primary?: boolean; note?: string | null }[];
    email?: string | null;
    tax_code?: string | null;
    birthday?: string | null;
    gender?: number | null; // 0 Nữ, 1 Nam, 2 Khác
    address?: string | null;
    is_foreign?: number; // 0 Trong nước, 1 Nước ngoài / Việt kiều
    source?: string | null;
    has_chat_customer?: number;
    connections?: {
        id: number;
        source?: string | null;
        channel_customers_id?: number;
        channel_connection?: { id: number; display_name?: string; channel_type_code?: string } | null;
        channel_customer?: { id: number; display_name?: string; channel_connection_id?: number; connection?: any } | null;
    }[];
    note?: string | null;
    created_at?: string;
    updated_at?: string;
}

export interface ChannelCustomerDetail {
    id: number;
    display_name: string;
    external_user_id: string;
    channel_connection_id?: number;
    connection?: {
        id: number;
        display_name?: string;
        channel_type_code?: string;
        oauth_account_id?: number;
        // type === 2 => Zalo OA (tin nhắn đi chung API channel-message như Facebook)
        oauth_account?: { id: number; provider?: string; type?: number };
    };
    phone: string;
    province_code: string | null;
    ward_code: string | null;
    address: string;
    note: string;
    customer_tag_id: number | null;
    care_status_id?: number | null;
    customer_care_status_id?: number | null;
    // Quan hệ nhiều-nhiều; customer_care_status_id chỉ là trạng thái chính
    care_statuses?: CustomerCareStatus[];
    customer_care_status_ids?: number[];
    customer_occupation_id?: number | null;
    assigned_user_id: number | null;
    occupation_id: number | null;
    email?: string | null;
    birthday?: string | null;
    gender?: number | null;
    care_status?: { id: number; name: string } | null;
    assigned_user?: { id: number; name: string } | null;
    occupation?: { id: number; name: string } | null;
    province?: { id: number; name: string; code: string } | null;
    ward?: { id: number; name: string; code: string } | null;
    avatar?: string | null;
    avatar_url?: string | null;
    profile_link?: string | null; // Link trang cá nhân của khách trên kênh (FB/Instagram...)
    last_message_text?: string | null;
    // Hướng của tin nhắn cuối: "outbound" = nhân viên gửi, "inbound" = khách gửi
    direction?: "inbound" | "outbound" | string | null;
    metadata?: any;
    is_ping?: number;
    is_spam?: number;
    unread_count?: number;
    previous_unread_count?: number;
    ad_info?: AdInfo | null;
    ad_json?: AdInfo | null;
    // Lịch sử quảng cáo: khách có thể tương tác nhiều quảng cáo khác nhau
    // (ad_info/ad_json chỉ là quảng cáo mới nhất)
    ad_histories?: ChannelCustomerAdHistory[] | null;
}

// Một lần khách vào từ quảng cáo; ad_json là chi tiết quảng cáo (object hoặc chuỗi JSON)
export interface ChannelCustomerAdHistory {
    id: number;
    channel_customer_id?: number;
    ad_id?: string | null;
    ad_json?: AdInfo | string | null;
    created_at?: string;
    updated_at?: string;
}

export interface ChannelCustomerDetailResponse {
    status: boolean;
    data: ChannelCustomerDetail | null;
}

export interface CustomerConnection {
    id: number;
    customer_id: number;
    channel_customers_id: number;
    // Mã nền tảng khách đến từ đó (fb, zalo, ...); trùng channel_type_code của trang
    source?: string;
    channel_connection_id?: number;
    channel_connection?: {
        id: number;
        name?: string;
        display_name?: string;
        channel_type_code?: string;
        [key: string]: any;
    };
    channel_customer?: {
        id: number;
        display_name?: string;
        channel_connection_id?: number;
        connection?: { id: number; display_name?: string; channel_type_code?: string };
        [key: string]: any;
    };
    created_at: string;
    updated_at: string;
}

export interface CustomerSpam {
    id: number;
    channel_customer_id:string;
    note: string;
    image_1?: string;
    image_2?:string;
    image_3?:string;
    created_at:string;
    creator?: {
        id: number;
        fullname:string;
    }
}

export interface CustomerCareStatus {
    id: number;
    name: string;
    code?: string | null;
    color?: string | null;
    description?: string | null;
    position?: number | null;
    is_active?: boolean;
    pivot?: { customer_id?: number; channel_customer_id?: number; customer_care_status_id: number };
}

export interface CustomerDetailData {
    id: number;
    customer_code: string | null;
    display_name: string;
    /** Họ tên thật của khách (display_name là tên hiển thị) */
    fullname?: string | null;
    phone: string | null;
    /** Số điện thoại phụ (API trả kèm ở chi tiết khách hàng) */
    phones?: CustomerPhoneEntry[] | null;
    email: string | null;
    birthday: string | null;
    gender: number | null;
    avatar: string | null;
    avatar_url?: string | null;
    address: string | null;
    province_code: string | null;
    ward_code: string | null;
    staff_id: number | null;
    customer_tag_id: number | null;
    customer_occupation_id: number | null;
    customer_care_status_id: number | null;
    care_status_id?: number | null;
    // Quan hệ nhiều-nhiều: một khách có thể mang nhiều trạng thái chăm sóc.
    // customer_care_status_id chỉ là trạng thái chính, giữ cho tương thích ngược.
    care_statuses?: CustomerCareStatus[];
    note: string | null;
    created_at: string;
    updated_at: string;
    deleted_at: string | null;
    connections: CustomerConnection[];
    staff: { id: number; name: string } | null;
    province?: { code: string; name: string } | null;
    ward?: { code: string; name: string } | null;
    customer_tag?: { id: number; name: string; color?: string | null } | null;
    /** Thẻ được phép chuyển sang, backend tính theo ma trận (phòng ban + screen_context = 4 "Khách hàng"). */
    dynamic_next_tags?: AppointmentNextTag[] | null;
    customer_care_status?: { id: number; name: string; color?: string } | null;
    customer_occupation?: { id: number; name: string } | null;
    // compat fields
    external_user_id?: string;
    assigned_user_id?: number | null;
    assigned_user?: { id: number; name: string } | null;
    occupation_id?: number | null;
    occupation?: { id: number; name: string } | null;
    metadata?: any;
    is_spam?: number;
    /** Nguồn gốc khách: 0 = trong nước, 1 = nước ngoài (khách nước ngoài không bắt buộc Tỉnh/Xã) */
    is_foreign?: number | null;
    spam_logs?: CustomerSpam[];
    // Báo cáo cuộc gọi (Telesale)
    in_charge_staff_id?: number | null;
    last_call_result?: number | null;
    last_call_result_text?: string | null;
    call_result_options?: { id: number; text: string }[];
}

export interface CustomerDetailResponse {
    status: boolean;
    data: CustomerDetailData | null;
    msg: string;
}

/**
 * Mở một hội thoại, nhiều khung (khung chat, thông tin khách, form, tạo lịch hẹn...) cùng lấy
 * chi tiết khách: gộp lại thành 1 request, dùng lại kết quả trong vài giây.
 * Mọi hàm sửa khách trong file này tự xoá cache; sửa qua đường khác thì gọi `invalidateCustomerCaches()`.
 */
const CUSTOMER_CACHE_TTL_MS = 5000;
const customerDetailCache = createRequestCache<CustomerDetailResponse>(CUSTOMER_CACHE_TTL_MS, (r) => r.status);
const channelCustomerDetailCache = createRequestCache<ChannelCustomerDetailResponse>(CUSTOMER_CACHE_TTL_MS, (r) => r.status);

/** Xoá cache chi tiết khách (CRM và khách kênh) sau khi sửa dữ liệu khách */
export function invalidateCustomerCaches() {
    customerDetailCache.invalidate();
    channelCustomerDetailCache.invalidate();
}

/** `fresh`: bỏ qua kết quả vừa lấy, gọi lại API (vd cần dữ liệu mới nhất ngay lúc gửi form) */
export function getCustomerDetail(id: number, opts?: { fresh?: boolean }): Promise<CustomerDetailResponse> {
    return customerDetailCache.get(String(id), () => fetchCustomerDetail(id), opts?.fresh);
}

async function fetchCustomerDetail(id: number): Promise<CustomerDetailResponse> {
    try {
        const token = localStorage.getItem('token') || '';
        const response = await fetch(`${API_URL}/api/v1/be/customers/${id}`, {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
        });
        if (!response.ok) {
            if (response.status === 401) { mixLogOut(); }
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        return await response.json() as CustomerDetailResponse;
    } catch (error) {
        console.error('Failed to fetch customer detail:', error);
        return { status: false, data: null, msg: '' };
    }
}

export interface ChannelCustomerListResponse {
    status: boolean;
    data: {
        items: ChannelCustomerItem[];
        total: number;
        current_page?: number;
    } | null;
}

/** Gắn các tham số lọc nâng cao vào query nếu có truyền (0 vẫn là giá trị hợp lệ) */
const appendAdvancedFilters = (query: URLSearchParams, params?: ChannelCustomerAdvancedFilters) => {
    if (!params) return;
    const numberKeys: (keyof ChannelCustomerAdvancedFilters)[] = [
        'is_unread', 'unanswered', 'read_unanswered', 'is_priority_contact', 'has_phone', 'is_spam', 'is_ping',
    ];
    numberKeys.forEach((key) => {
        const value = params[key];
        if (value !== undefined && value !== null) query.append(key, String(value));
    });
    if (params.assignment_status) query.append('assignment_status', params.assignment_status);
    if (params.last_seen_start_date) query.append('last_seen_start_date', params.last_seen_start_date);
    if (params.last_seen_end_date) query.append('last_seen_end_date', params.last_seen_end_date);
};

/** Bộ lọc nâng cao của API danh sách khách kênh (xem tài liệu: mục "Ví dụ thử nghiệm bộ lọc nâng cao") */
export interface ChannelCustomerAdvancedFilters {
    /** 1 = chỉ lấy hội thoại còn tin chưa đọc */
    is_unread?: number;
    /** 1 = khách nhắn cuối mà chưa được trả lời */
    unanswered?: number;
    /** 1 = đã đọc nhưng chưa trả lời */
    read_unanswered?: number;
    /** 1 = khách đánh dấu ưu tiên liên hệ */
    is_priority_contact?: number;
    /** 1 = có SĐT, 0 = chưa có SĐT */
    has_phone?: number;
    is_spam?: number;
    is_ping?: number;
    /** 'assigned' | 'unassigned' */
    assignment_status?: string;
    last_seen_start_date?: string;
    last_seen_end_date?: string;
    /**
     * Sắp xếp dạng chuỗi cho các giá trị đặc biệt: 'last_seen_asc' (đợi phản hồi lâu nhất)
     * hoặc 'last_seen_desc'. API KHÔNG hiểu các giá trị này khi gửi dạng sort[].
     */
    sortRaw?: string;
}

export async function getChannelCustomers(params?: {
    channel_connection_id?: number;
    search?: string;
    sort?: string[];
    customer_care_status_id?: number;
    customer_tag_id?: number;
    type?: string; // 'chat' | 'comment' | undefined (mặc định lấy tất cả)
    page?: number;
    limit?: number;
} & ChannelCustomerAdvancedFilters): Promise<ChannelCustomerListResponse> {
    try {
        const token = localStorage.getItem('token') || '';
        const query = new URLSearchParams();
        if (params?.type) query.append('type', params.type);
        if (params?.channel_connection_id) query.append('channel_connection_id', String(params.channel_connection_id));
        if (params?.search) query.append('search', params.search);
        if (params?.customer_care_status_id) query.append('customer_care_status_id', String(params.customer_care_status_id));
        if (params?.customer_tag_id) query.append('customer_tag_id', String(params.customer_tag_id));
        if (params?.page) query.append('page', String(params.page));
        if (params?.limit) query.append('limit', String(params.limit));
        appendAdvancedFilters(query, params);
        if (params?.sortRaw) {
            // Giá trị đặc biệt phải đi dạng sort=... (sort[]=... trả về rỗng)
            query.append('sort', params.sortRaw);
        } else if (params?.sort && params.sort.length > 0) {
            params.sort.forEach(s => query.append('sort[]', s));
        } else {
            query.append('sort[]', 'created_at,desc');
        }
        const queryString = query.toString();

        const response = await fetch(`${API_URL}/api/v1/be/channel-customers${queryString ? `?${queryString}` : ''}`, {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
        });

        if (!response.ok) {
            if (response.status === 401) { mixLogOut(); }
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        return await response.json() as ChannelCustomerListResponse;
    } catch (error) {
        console.error('Failed to fetch channel customers:', error);
        return {
            status: false,
            data: null,
        };
    }
}

// Danh sách khách hàng thuộc mọi kết nối kênh nằm trong một nhóm kênh
/**
 * Lưu ý: endpoint by-group hiện chỉ lọc được is_spam và is_priority_contact;
 * các tham số lọc nâng cao khác (is_unread, unanswered, type...) backend đang bỏ qua.
 * Vẫn gửi lên để khi backend bổ sung là chạy được ngay.
 */
export async function getChannelCustomersByGroup(groupId: number, params?: {
    search?: string;
    customer_care_status_id?: number;
    type?: string;
    sort?: string[];
    page?: number;
    limit?: number;
} & ChannelCustomerAdvancedFilters): Promise<ChannelCustomerListResponse> {
    try {
        const token = localStorage.getItem('token') || '';
        const query = new URLSearchParams();
        if (params?.type) query.append('type', params.type);
        if (params?.search) query.append('search', params.search);
        if (params?.customer_care_status_id) query.append('customer_care_status_id', String(params.customer_care_status_id));
        appendAdvancedFilters(query, params);
        if (params?.sortRaw) {
            query.append('sort', params.sortRaw);
        } else if (params?.sort && params.sort.length > 0) {
            params.sort.forEach(s => query.append('sort[]', s));
        }
        if (params?.page) query.append('page', String(params.page));
        if (params?.limit) query.append('limit', String(params.limit));
        const queryString = query.toString();

        const response = await fetch(`${API_URL}/api/v1/be/channel-customers/by-group/${groupId}${queryString ? `?${queryString}` : ''}`, {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
        });

        if (!response.ok) {
            if (response.status === 401) { mixLogOut(); }
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        return await response.json() as ChannelCustomerListResponse;
    } catch (error) {
        console.error('Failed to fetch channel customers by group:', error);
        return {
            status: false,
            data: null,
        };
    }
}

/** Chi tiết khách kênh, gộp các lần gọi trùng (xem `channelCustomerDetailCache`) */
export function getChannelCustomerDetail(id: number, opts?: { fresh?: boolean }): Promise<ChannelCustomerDetailResponse> {
    return channelCustomerDetailCache.get(String(id), () => fetchChannelCustomerDetail(id), opts?.fresh);
}

async function fetchChannelCustomerDetail(id: number): Promise<ChannelCustomerDetailResponse> {
    try {
        const token = localStorage.getItem('token') || '';
        const response = await fetch(`${API_URL}/api/v1/be/channel-customer/${id}`, {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
        });

        if (!response.ok) {
            if (response.status === 401) { mixLogOut(); }
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        return await response.json() as ChannelCustomerDetailResponse;
    } catch (error) {
        console.error('Failed to fetch channel customer detail:', error);
        return {
            status: false,
            data: null,
        };
    }
}

// Lịch sử spam của khách hàng kênh
export async function getChannelCustomerSpamLogs(id: number): Promise<{ status: boolean; data: CustomerSpam[]; msg?: string }> {
    try {
        const token = localStorage.getItem('token') || '';
        const response = await fetch(`${API_URL}/api/v1/be/channel-customer/${id}/spam-logs`, {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
        });
        const json = await response.json().catch(() => ({}));
        if (!response.ok) {
            if (response.status === 401) { mixLogOut(); }
            return { status: false, data: [], msg: json?.msg };
        }
        return { status: json?.status ?? true, data: Array.isArray(json?.data) ? json.data : [], msg: json?.msg };
    } catch (error) {
        console.error('Failed to fetch spam logs:', error);
        return { status: false, data: [] };
    }
}

// Đánh dấu đã đọc tin nhắn (đưa unread_count về 0, lưu previous_unread_count để có thể khôi phục)
export async function markReadChannelCustomer(id: number): Promise<{ status: boolean; data?: any; msg?: string }> {
    try {
        const token = localStorage.getItem('token') || '';
        const response = await fetch(`${API_URL}/api/v1/be/channel-customer/${id}/mark-read`, {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
        });
        invalidateCustomerCaches();
        const json = await response.json().catch(() => ({}));
        if (!response.ok) {
            if (response.status === 401) { mixLogOut(); }
            return { status: false, msg: json?.msg };
        }
        return { status: json?.status ?? true, data: json?.data, msg: json?.msg };
    } catch (error) {
        console.error('Failed to mark read:', error);
        return { status: false };
    }
}

// Khôi phục số tin nhắn chưa đọc (khi nhân viên lỡ đánh dấu đã đọc)
export async function restoreUnread(id: number): Promise<{ status: boolean; data?: any; msg?: string }> {
    try {
        const token = localStorage.getItem('token') || '';
        const response = await fetch(`${API_URL}/api/v1/be/channel-customer/${id}/restore-unread`, {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
        });
        invalidateCustomerCaches();
        const json = await response.json().catch(() => ({}));
        if (!response.ok) {
            if (response.status === 401) { mixLogOut(); }
            return { status: false, msg: json?.msg };
        }
        return { status: json?.status ?? true, data: json?.data, msg: json?.msg };
    } catch (error) {
        console.error('Failed to restore unread:', error);
        return { status: false };
    }
}

export interface ChannelCustomerPhoto {
    id: number;
    channel_customer_id: number;
    channel_message_id?: number | null;
    image_path: string;
    filename?: string | null;
    created_at?: string;
    updated_at?: string;
}

// Danh sách hình ảnh khách hàng theo kênh
// Lưu ý: hiện backend trả về toàn bộ ảnh (bỏ qua page/limit). Vẫn gửi sẵn để
// khi backend hỗ trợ phân trang là dùng được ngay.
export async function getChannelCustomerPhotos(
    id: number,
    params?: { page?: number; limit?: number }
): Promise<{ status: boolean; data: ChannelCustomerPhoto[] }> {
    try {
        const token = localStorage.getItem('token') || '';
        const query = new URLSearchParams();
        if (params?.page) query.append('page', String(params.page));
        if (params?.limit) query.append('limit', String(params.limit));
        const qs = query.toString();
        const response = await fetch(`${API_URL}/api/v1/be/channel-customers/${id}/photos${qs ? `?${qs}` : ''}`, {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
        });
        if (!response.ok) {
            if (response.status === 401) { mixLogOut(); }
            return { status: false, data: [] };
        }
        const json = await response.json().catch(() => ({}));
        return { status: json?.status ?? true, data: Array.isArray(json?.data) ? json.data : [] };
    } catch (error) {
        console.error('Failed to fetch channel customer photos:', error);
        return { status: false, data: [] };
    }
}

export async function updateChannelCustomer(id: number, data: Partial<ChannelCustomerDetail>): Promise<{ status: boolean }> {
    try {
        const token = localStorage.getItem('token') || '';
        const response = await fetch(`${API_URL}/api/v1/be/channel-customer/${id}`, {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify(data),
        });
        invalidateCustomerCaches();

        if (!response.ok) {
            if (response.status === 401) { mixLogOut(); }
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        return await response.json();
    } catch (error) {
        console.error('Failed to update channel customer:', error);
        return { status: false };
    }
}

export interface UpdateCustomerPayload {
    display_name?: string;
    /** Họ tên thật (display_name là tên hiển thị) */
    fullname?: string | null;
    customer_code?: string;
    phone?: string;
    email?: string;
    gender?: number | null;
    /** Nguồn gốc khách: 0 = trong nước, 1 = nước ngoài */
    is_foreign?: number | null;
    birthday?: string | null;
    address?: string | null;
    province_code?: string | null;
    ward_code?: string | null;
    staff_id?: number | null;
    telesale_id?: number | null;
    customer_tag_id?: number | null;
    customer_occupation_id?: number | null;
    customer_care_status_id?: number | null;
    // Gán nhiều trạng thái chăm sóc cùng lúc (VD: [13, 14]).
    // Gửi kèm customer_care_status_id (phần tử đầu) để backend cũ vẫn hiểu.
    customer_care_status_ids?: number[];
    note?: string | null;
    channel_customers_ids?: number[];
    last_call_result?: number | null;
    // Danh sách số điện thoại phụ; gửi cả mảng, backend ghi đè danh sách hiện có
    phones?: CustomerPhoneEntry[];
}

/** Một số điện thoại phụ của khách (theo tài liệu API: phone bắt buộc, is_primary/note tùy chọn) */
export interface CustomerPhoneEntry {
    id?: number;
    phone: string;
    is_primary?: boolean | number;
    note?: string | null;
}

export async function updateCustomer(id: number, data: UpdateCustomerPayload): Promise<{ status: boolean; msg?: string }> {
    try {
        const token = localStorage.getItem('token') || '';

        const response = await fetch(`${API_URL}/api/v1/be/customers/${id}`, {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify(data),
        });
        invalidateCustomerCaches();

        if (!response.ok) {
            if (response.status === 401) { mixLogOut(); }
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        return await response.json();
    } catch (error) {
        console.error('Failed to update customer:', error);
        return { status: false };
    }
}

export async function markSpamChannelCustomer(
    id: number,
    data: { is_spam: number; note?: string; image_1?: File; image_2?: File; image_3?: File }
): Promise<{ status: boolean; message?: string }> {
    try {
        const token = localStorage.getItem('token') || '';
        const formData = new FormData();
        formData.append('is_spam', String(data.is_spam));
        if (data.note) formData.append('note', data.note);
        if (data.image_1) formData.append('image_1', data.image_1);
        if (data.image_2) formData.append('image_2', data.image_2);
        if (data.image_3) formData.append('image_3', data.image_3);

        const response = await fetch(`${API_URL}/api/v1/be/channel-customer/${id}/mark-spam`, {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
            body: formData,
        });
        invalidateCustomerCaches();

        if (!response.ok) {
            if (response.status === 401) { mixLogOut(); }
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        return await response.json();
    } catch (error) {
        console.error('Failed to mark spam:', error);
        return { status: false };
    }
}

// Đánh dấu / gỡ spam ở cấp KHÁCH HÀNG TỔNG (khác với channel-customer).
// is_spam=1: đánh dấu (bắt buộc note). is_spam=0: gỡ spam, xóa sạch logs của tất cả tài khoản chat liên kết.
export async function markSpamCustomer(
    id: number,
    data: { is_spam: number; note?: string; image_1?: File; image_2?: File; image_3?: File }
): Promise<{ status: boolean; message?: string; msg?: string }> {
    try {
        const token = localStorage.getItem('token') || '';
        const formData = new FormData();
        formData.append('is_spam', String(data.is_spam));
        if (data.note) formData.append('note', data.note);
        if (data.image_1) formData.append('image_1', data.image_1);
        if (data.image_2) formData.append('image_2', data.image_2);
        if (data.image_3) formData.append('image_3', data.image_3);

        const response = await fetch(`${API_URL}/api/v1/be/customers/${id}/mark-spam`, {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
            body: formData,
        });
        invalidateCustomerCaches();

        const json = await response.json().catch(() => ({}));
        if (!response.ok) {
            if (response.status === 401) { mixLogOut(); }
            return { status: false, msg: json?.msg };
        }
        return { status: json?.status ?? true, message: json?.message, msg: json?.msg };
    } catch (error) {
        console.error('Failed to mark spam customer:', error);
        return { status: false };
    }
}

export async function uploadChannelCustomerAvatar(id: number, file: File, displayName?: string): Promise<{ status: boolean; data?: any }> {
    try {
        const token = localStorage.getItem('token') || '';

        const formData = new FormData();
        formData.append('avatar', file);
        if (displayName) formData.append('display_name', displayName);

        const response = await fetch(`${API_URL}/api/v1/be/customers/${id}`, {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
            body: formData,
        });
        invalidateCustomerCaches();

        if (!response.ok) {
            if (response.status === 401) { mixLogOut(); }
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        return await response.json();
    } catch (error) {
        console.error('Failed to upload customer avatar:', error);
        return { status: false };
    }
}
// Cập nhật avatar khách hàng KÊNH: POST /api/v1/be/channel-customer/{id} (multipart, field "avatar")
export async function updateChannelCustomerAvatar(id: number, file: File): Promise<{ status: boolean; data?: any; msg?: string }> {
    try {
        const token = localStorage.getItem('token') || '';
        const formData = new FormData();
        formData.append('avatar', file);

        const response = await fetch(`${API_URL}/api/v1/be/channel-customer/${id}`, {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
            body: formData,
        });
        invalidateCustomerCaches();
        const json = await response.json().catch(() => ({}));
        if (!response.ok) {
            if (response.status === 401) { mixLogOut(); }
            return { status: false, msg: json?.msg };
        }
        return { status: json?.status ?? true, data: json?.data, msg: json?.msg };
    } catch (error) {
        console.error('Failed to update channel customer avatar:', error);
        return { status: false };
    }
}

export async function deleteCustomerApi(id: number): Promise<{ status: boolean; msg?: string }> {
    try {
        const token = localStorage.getItem('token') || '';
        const response = await fetch(`${API_URL}/api/v1/be/customers/${id}`, {
            method: 'DELETE',
            headers: {
                'Accept': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
        });
        invalidateCustomerCaches();
        if (!response.ok) {
            if (response.status === 401) { mixLogOut(); }
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        return await response.json();
    } catch (error) {
        console.error('Failed to delete customer:', error);
        return { status: false };
    }
}